<template>
  <div class="exam-page">
    <div v-if="loading" class="status-card">正在加载考试...</div>
    <div v-else-if="errorMessage" class="status-card is-error">{{ errorMessage }}<button class="btn btn--secondary" @click="$router.back()">返回</button></div>
    <template v-else-if="examData.record_id">
      <section class="exam-header panel">
        <div class="exam-header__info">
          <h1>{{ examData.paper_title }}</h1>
          <p class="exam-course">{{ examData.course_title }} - {{ examData.target_title }}</p>
          <div class="exam-meta">
            <span>{{ examData.question_count }} 题</span><span>总分 {{ examData.total_score }} 分</span><span>及格 {{ examData.pass_score }} 分</span>
            <span v-if="examData.exam_minutes > 0">限时 {{ examData.exam_minutes }} 分钟</span>
          </div>
          <p v-if="examData.description" class="exam-description">{{ examData.description }}</p>
        </div>
        <div v-if="examData.exam_minutes > 0" class="exam-timer"><div class="timer-circle"><span class="timer-time">{{ formatTime(remainingSeconds) }}</span><span class="timer-label">剩余时间</span></div></div>
      </section>
      <section class="exam-questions">
        <QuestionCard
          v-for="(question, index) in questions"
          :key="question.question_id"
          :question="question"
          :index="index"
          :value="answers[question.question_id]"
          :disabled="submitting"
          @input="$set(answers, question.question_id, $event)"
        />
      </section>
      <section class="exam-actions panel">
        <button class="btn btn--secondary" @click="handleCancel">取消考试</button>
        <button class="btn btn--primary" :disabled="submitting" @click="handleSubmit">{{ submitting ? '提交中...' : '提交答案' }}</button>
      </section>
    </template>
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { startExam, submitExam } from '@/api/courseProcess'
import AuthModal from '@/components/AuthModal.vue'
import QuestionCard from '@/components/question/QuestionCard.vue'
import authErrorHandler from '@/mixins/authErrorHandler'
import { createCanonicalAnswer, isAnswerComplete, serializeExamAnswer } from '@/utils/questionAnswer'

export default {
  name: 'ExamView',
  components: { AuthModal, QuestionCard },
  mixins: [authErrorHandler],
  props: { processExamId: { type: [Number, String], required: true } },
  data() { return { loading: true, errorMessage: '', submitting: false, examData: {}, answers: {}, remainingSeconds: 0, timer: null } },
  computed: { questions() { return Array.isArray(this.examData.questions) ? this.examData.questions : [] } },
  created() { this.loadExam() },
  beforeDestroy() { if (this.timer) clearInterval(this.timer) },
  methods: {
    async loadExam() {
      this.loading = true
      this.errorMessage = ''
      try {
        this.examData = await startExam(Number(this.processExamId))
        this.initAnswers()
        if (this.examData.exam_minutes > 0) this.startTimer(this.examData.started_at, this.examData.exam_minutes)
      } catch (error) {
        console.error('加载考试失败:', error)
        if (this.isAuthError(error)) this.handleAuthError()
        else this.errorMessage = error.message || '加载考试失败，请稍后重试'
      } finally { this.loading = false }
    },
    initAnswers() {
      const answers = {}
      this.questions.forEach(question => { answers[question.question_id] = createCanonicalAnswer(question) })
      this.answers = answers
    },
    startTimer(startedAt, examMinutes) {
      const startTime = new Date(startedAt).getTime()
      const duration = examMinutes * 60 * 1000
      const updateTimer = () => {
        const remaining = Math.max(0, duration - (Date.now() - startTime))
        this.remainingSeconds = Math.floor(remaining / 1000)
        if (remaining <= 0) { clearInterval(this.timer); window.alert('考试时间已到，系统将自动提交答案'); this.handleSubmit() }
      }
      updateTimer()
      this.timer = setInterval(updateTimer, 1000)
    },
    formatTime(seconds) {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60
      return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}` : `${minutes}:${String(secs).padStart(2, '0')}`
    },
    handleCancel() { if (window.confirm('确定要取消考试吗？当前答题进度将不会保存。')) this.$router.back() },
    async handleSubmit() {
      if (this.submitting) return
      const unansweredCount = this.questions.filter(question => !isAnswerComplete(question, this.answers[question.question_id])).length
      if (unansweredCount > 0 && !window.confirm(`还有 ${unansweredCount} 题未作答，确定要提交吗？`)) return
      this.submitting = true
      try {
        const answers = this.questions.map(question => ({ question_id: question.question_id, answer: serializeExamAnswer(question, this.answers[question.question_id]) }))
        const result = await submitExam({ record_id: this.examData.record_id, answers })
        if (this.timer) clearInterval(this.timer)
        this.$router.replace({ name: 'exam-result', params: { recordId: result.id } })
      } catch (error) {
        console.error('提交考试失败:', error)
        if (this.isAuthError(error)) this.handleAuthError()
        else window.alert(error.message || '提交失败，请稍后重试')
      } finally { this.submitting = false }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';
.exam-page { max-width: 1000px; margin: 0 auto; padding: 20px; }
.status-card { padding: 40px; border-radius: 12px; background: #f5f7fa; color: #606266; text-align: center; }
.status-card.is-error { background: #fef0f0; color: #f56c6c; }
.status-card .btn { margin-top: 16px; }
.exam-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 20px; padding: 24px; }
.exam-header__info { flex: 1; }
.exam-header h1 { font-size: 28px; }
.exam-course, .exam-description { margin-top: 8px; color: $color-text-secondary; }
.exam-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
.exam-meta span { padding: 6px 12px; border-radius: var(--radius-lg); background: $color-background; color: $color-text-secondary; font-size: 13px; }
.exam-timer { flex: 0 0 auto; }
.timer-circle { display: flex; width: 120px; height: 120px; align-items: center; justify-content: center; flex-direction: column; border: 5px solid $color-primary; border-radius: 50%; background: $color-surface; }
.timer-time { color: $color-primary; font-size: 22px; font-weight: 800; font-variant-numeric: tabular-nums; }
.timer-label { color: $color-text-secondary; font-size: 12px; }
.exam-questions { display: grid; gap: 20px; }
.exam-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; padding: 24px; }
@media (max-width: 768px) { .exam-page { padding: 12px; } .exam-header, .exam-actions { align-items: stretch; flex-direction: column; } .exam-timer { align-self: center; } .exam-actions .btn { width: 100%; } }
</style>
